import { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Table from '@/components/Tables/Table/Table';
import { StoreProduct } from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import styles from './ProductsTable.module.css';

interface ProductsTableProps {
  storeProducts: StoreProduct[];
  setPriceDeviationsData: (data: {
    isPriceDeviationsDrawerOpen: boolean;
    variantIds: never[];
    selectedGroupName: string;
    cameFromEditDrawer: boolean;
  }) => void;
}

interface ProductRow {
  productName: string;
  productDescription: string;
  variantId?: string;
  [key: string]: any; // For dynamic store group columns
}

const ProductsTable: FC<ProductsTableProps> = ({ storeProducts, setPriceDeviationsData }) => {
  const { t } = useTranslation();
  const { storesAndGroups } = useSTKContext();

  // Get all store group names from the user's selection (context)
  // This is what's actually displayed in the main table
  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    // Extract store group names based on origin
    if (storesAndGroups.origin === 'tab-store') {
      // When selecting individual stores, get unique store group names
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet);
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      // When selecting store groups, get the group names
      return storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || [];
    }

    return [];
  }, [storesAndGroups]);

  // Get store counts from context (same as main table)
  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  // Build table data - just use the data directly from products
  const tableData = useMemo(() => {
    if (!storeProducts || storeProducts.length === 0) {
      return [];
    }

    return storeProducts.map((product) => {
      const row: ProductRow = {
        productName: product.productName || '',
        productDescription: product.productDescription || '',
        variantId: product.variantId,
      };

      // For each store group name, get the price from the product's storeGroups array
      allStoreGroupNames.forEach((storeGroupName) => {
        const storeGroupData = product.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName);

        if (!storeGroupData || !storeGroupData.chosenPriceRange) {
          // No data for this store group - show "--"
          row[storeGroupName] = 'Inactive';
        } else {
          // Has data - format the price exactly as shown in the previous page
          const priceRange = storeGroupData.chosenPriceRange;
          let formattedPrice;

          // Special handling for "Inactive" status with price
          if (priceRange.startsWith('Inactive-')) {
            const price = priceRange.replace('Inactive-', '');
            formattedPrice = `${t('common.inactive')} - $${price}`;
          } else if (priceRange === 'Inactive') {
            // Display "Inactive" without $ sign
            formattedPrice = priceRange;
          } else {
            formattedPrice = `$${priceRange}`.replace(/-/g, ' - ');
          }

          // Store both the display value and metadata for rendering
          row[storeGroupName] = {
            display: formattedPrice,
            hasRange: formattedPrice.includes('-'),
            groupName: storeGroupName,
            variantId: product.variantId,
          };
        }
      });

      return row;
    });
  }, [storeProducts, allStoreGroupNames, t]);

  // Build columns - one for product name, one for each store group
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        id: 'productName',
        accessorKey: 'productName',
        header: 'PRODUCT',
        cell: (info: any) => (
          <div>
            <div className={styles.productName}>{info.getValue()}</div>
            <div className={styles.productDescription}>{info.row.original.productDescription}</div>
          </div>
        ),
        size: 350,
        minSize: 250,
        maxSize: 450,
      },
    ];

    // Add a column for each store group
    allStoreGroupNames.forEach((storeGroupName, index) => {
      const { count, total } = getStoreGroupsCount(storeGroupName);

      cols.push({
        id: `${storeGroupName}_${index}`,
        accessorKey: storeGroupName,
        header: () => (
          <div className={styles.headerWithCount}>
            <div className={styles.headerName}>{storeGroupName.toUpperCase()}</div>
            <div className={styles.headerCount}>
              {count} / {total}
            </div>
          </div>
        ),
        size: 145,
        minSize: 130,
        maxSize: 160,
        cell: (info: any) => {
          const cellData = info.getValue();

          // If it's a string (just "--"), display it
          if (typeof cellData === 'string') {
            return <span className={styles.inactive}>{cellData}</span>;
          }

          // If it's null or undefined, show "--"
          if (!cellData) {
            return <span className={styles.inactive}>--</span>;
          }

          // If it has a range, show with info icon
          if (cellData.hasRange) {
            return (
              <div className={styles.priceWithIcon}>
                <span>{cellData.display}</span>
                <InfoOutlinedIcon
                  className={styles.infoIcon}
                  onClick={() => {
                    if (cellData.variantId && cellData.groupName) {
                      setPriceDeviationsData({
                        selectedGroupName: cellData.groupName,
                        variantIds: [cellData.variantId] as never[],
                        isPriceDeviationsDrawerOpen: true,
                        cameFromEditDrawer: true,
                      });
                    }
                  }}
                  sx={{
                    width: '18px',
                    height: '18px',
                    color: '#266DCC',
                    cursor: 'pointer',
                    marginLeft: '4px',
                  }}
                />
              </div>
            );
          }

          // Single price - just display it
          return <span>{cellData.display}</span>;
        },
      });
    });

    return cols;
  }, [allStoreGroupNames, getStoreGroupsCount, setPriceDeviationsData]);

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <Table data={tableData} columns={columns} height="calc(85vh - 350px)" />
      </div>
    </div>
  );
};

export default ProductsTable;
